/* Latest React refs and the synchronous submission lock are instance-owned. */
/* eslint-disable @silverhand/fp/no-mutation */
import { AgreeToTermsPolicy } from '@logto/schemas';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SocialLinkButton from '@/components/Button/SocialLinkButton';
import ContextualSignInForm from '@/components/ContextualSignInForm';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useTerms from '@/hooks/use-terms';
import type { ContextualSignInPage } from '@/utils/contextual-sign-in-page';

import QrCode from './QrCode';
import { mountOfficialWechatQR, openOfficialWechatWindow } from './browser';
import { createWechatQRController } from './controller';
import { wechatCopy, wechatNotice } from './copy';
import styles from './index.module.scss';
import { createNativeWechatAPI } from './native';
import { readWechatNavigation, type WechatFlow } from './protocol';
import { selectedWechatConnector } from './selection';

export const QRSession = ({
  connectorId,
  flow,
  isEmbedded,
  isMobile,
  locale,
  onOtherMethod,
}: {
  readonly connectorId: string;
  readonly flow: WechatFlow;
  readonly isEmbedded: boolean;
  readonly isMobile: boolean;
  readonly locale: string;
  readonly onOtherMethod: () => void;
}) => {
  const captcha = useContext(CaptchaContext);
  const interaction = useContext(UserInteractionContext);
  const current = useRef({ captcha, interaction });
  current.current = { captcha, interaction };
  // This state initializer retains one controller for the component lifetime.
  // eslint-disable-next-line react/hook-use-state
  const [controller] = useState<ReturnType<typeof createWechatQRController>>(() => {
    const native = createNativeWechatAPI({
      connectorId,
      isCaptchaRequired: () => current.current.captcha.isCaptchaRequired,
      executeCaptcha: async () => current.current.captcha.executeCaptcha(),
      setVerificationId: (type, id) => {
        current.current.interaction.setVerificationId(type, id);
      },
    });
    return createWechatQRController(flow, {
      ...native,
      mount: mountOfficialWechatQR,
      openWindow: openOfficialWechatWindow,
    });
  });
  return (
    <QrCode
      controller={controller}
      isEmbedded={isEmbedded}
      isMobile={isMobile}
      isBinding={flow === 'binding'}
      locale={locale}
      onOtherMethod={onOtherMethod}
    />
  );
};

/** Extends the original native form; it does not own credentials or account creation. */
const WechatSignIn = ({
  settings,
  isEmbedded,
}: {
  readonly settings: ContextualSignInPage;
  readonly isEmbedded: boolean;
}) => {
  const { experienceSettings, platform } = useContext(PageContext);
  const { agreeToTermsPolicy, termsValidation } = useTerms();
  const { i18n } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigateWithPreservedSearchParams();
  const [requested, setRequested] = useState(false);
  const [isFocusRequested, setIsFocusRequested] = useState(false);
  const [checkingTerms, setCheckingTerms] = useState(false);
  const pending = useRef(false);
  const connector = selectedWechatConnector(
    experienceSettings?.socialConnectors ?? [],
    Boolean(settings.options.wechat_enabled)
  );
  const hint = connector ? readWechatNavigation(state) : undefined;
  const qr = requested || hint?.step === 'binding-qr' || hint?.step === 'sign-in-qr';
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const copy = wechatCopy(locale);
  const other = () => {
    setIsFocusRequested(true);
    setRequested(false);
    navigate('/sign-in', { replace: true, state: null });
  };
  async function start() {
    if (pending.current || !connector) {
      return;
    }
    pending.current = true;
    setCheckingTerms(true);
    try {
      if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
        return;
      }
      setRequested(true);
    } finally {
      pending.current = false;
      setCheckingTerms(false);
    }
  }
  if (connector && qr) {
    return (
      <QRSession
        key={connector.id + (hint?.step === 'binding-qr' ? ':binding' : ':sign-in')}
        connectorId={connector.id}
        flow={hint?.step === 'binding-qr' ? 'binding' : 'sign-in'}
        isEmbedded={isEmbedded}
        isMobile={platform === 'mobile'}
        locale={locale}
        onOtherMethod={other}
      />
    );
  }
  if (connector && hint?.step === 'error') {
    return (
      <section className={styles.panel}>
        <h1>{copy.title}</h1>
        <p className={styles.error} role="alert">
          {wechatNotice(hint.notice ?? 'unavailable', locale)}
        </p>
        <button className={styles.secondary} type="button" onClick={other}>
          {copy.other}
        </button>
      </section>
    );
  }
  return (
    <>
      <ContextualSignInForm
        {...settings}
        isEmbedded={isEmbedded}
        isFocusRequested={isFocusRequested}
        {...(hint?.step === 'account-proof'
          ? { continuation: 'wechat-account-proof' as const }
          : {})}
      />
      {connector && hint?.step !== 'account-proof' && (
        <section className={styles.otherMethods} aria-label="其他登录方式">
          <p>其他登录方式</p>
          {agreeToTermsPolicy === AgreeToTermsPolicy.Manual && <TermsAndPrivacyCheckbox />}
          <SocialLinkButton
            target={connector.target}
            name={connector.name}
            logo={connector.logo}
            isDisabled={checkingTerms}
            isLoading={checkingTerms}
            onClick={() => {
              void start();
            }}
          />
        </section>
      )}
    </>
  );
};
export default WechatSignIn;

/* eslint-enable @silverhand/fp/no-mutation */
